import { useRef } from 'react';
import { useMemoizedFn } from 'ahooks';
import { isEqual, omit } from 'lodash-es';
import { numberComparator } from '@intelligence/utils/commonUtils';
import { SortType, SortMode } from '@intelligence/pages/TopCharts/_config/enums';

import type { ICommonEntity } from '@intelligence/types/common';

// 常用字段配置
interface ICommonFieldsMapping {
  // 列表数据的主键字段名
  primaryKeyField?: string;
  // 列表数据的名称字段名
  nameField?: string;
}

// 排序相关字段映射配置
interface IOrderFieldsMapping {
  // 排序指标数据源字段名
  orderSourceField: string;
  // 排序指标字段名
  orderMetricField: string;
  // 排序环比开关字段名
  orderRatioField: string;
  // 排序方式字段名
  orderField: string;
}

// 分页相关字段映射配置
interface IPaginationFieldsMapping {
  // 分页pageIndex字段名
  pageIndexField?: string;
  // 分页pageSize字段名
  pageSizeField?: string;
  // 分页total字段名
  totalField?: string;
  // 当前分页列表数据字段名
  listField?: string;
}

// 搜索相关字段映射配置
interface ISearchFieldMapping {
  searchKeywordField?: string;
  searchIdsField?: string;
}

interface IOrderInfo {
  orderSource: string;
  orderMetric: string;
  orderRatio: SortMode;
  order: SortType;
}

// 分页配置、排序配置
interface IUserClientPaginationAndOrderProps {
  // 常用字段映射配置
  commonFieldsMapping?: ICommonFieldsMapping;
  // 排序相关字段映射配置
  orderFieldsMapping?: IOrderFieldsMapping;
  // 分页相关字段映射配置
  paginationFieldsMapping?: IPaginationFieldsMapping;
  // 搜索相关字段映射配置
  searchFieldsMapping?: ISearchFieldMapping;
  // 需要用res的req中 回写params参数的字段
  rewriteFields?: string[];
  // 排序comparator工厂方法
  buildComparator?: (orderInfo: IOrderInfo) => (a: any, b: any) => number;
  // 刷新列表数据的回调，回调参数为接口返回的列表数据、请求接口时的入参
  refreshCallback?: (list: any[], params: any) => void;
  // 列表数据处理方法
  handler?: (list: any[]) => any[];
}

export default function useClientPaginationAndOrder(
  // 拉取数据接口service
  service: (params: any) => Promise<any>,
  // 分页配置、排序配置
  props?: IUserClientPaginationAndOrderProps,
) {
  const {
    commonFieldsMapping,
    orderFieldsMapping,
    paginationFieldsMapping,
    searchFieldsMapping,
    rewriteFields = ['granularity', 'start_date', 'end_date'],
    buildComparator,
    refreshCallback,
    handler,
  } = props || {};

  const { primaryKeyField = 'id', nameField = 'name' } = commonFieldsMapping || {};

  const {
    orderSourceField = 'order_source',
    orderMetricField = 'order_metric',
    orderRatioField = 'ratio_type',
    orderField = 'order',
  } = orderFieldsMapping || {};

  const {
    pageIndexField = 'page',
    pageSizeField = 'page_size',
    totalField = 'total',
    listField = 'list',
  } = paginationFieldsMapping || {};

  const { searchKeywordField = 'search_by_name', searchIdsField = 'search_by_ids' } = searchFieldsMapping || {};

  // 缓存数据
  const cachedData = useRef<{
    // 缓存最新请求列表数据的参数（不包含分页、排序相关的字段）
    params: ICommonEntity;
    // 缓存最新请求返回的列表数据total
    total: number;
    // 缓存最新请求返回的列表数据
    list: any[];
    // 缓存最新请求返回的整个res数据（除去data字段）
    res: ICommonEntity;
  }>({
    params: {},
    total: 0,
    list: [],
    res: {},
  });

  // 回写params参数
  const rewriteParams = useMemoizedFn((params: ICommonEntity, req: ICommonEntity): ICommonEntity => {
    if (!params) return {};
    if (!req) return params;
    if (!rewriteFields?.length) return params;
    const rewriteParams = rewriteFields.reduce<ICommonEntity>((current, next) => {
      if (!next) return current;
      return {
        ...current,
        [next]: req?.[next],
      };
    }, {});
    return { ...params, ...rewriteParams };
  });

  // 重新请求接口，刷新缓存数据
  const refreshCachedData = useMemoizedFn(async (params: ICommonEntity) => {
    let res;
    let resData;
    let resReq;
    try {
      res = await service(params);
      resReq = res?.req || {};
      resData = res?.data || {};
      const resList = resData?.[listField] || [];
      let data = handler?.(resList) || resList;
      data = Array.isArray(data) ? data : [];
      cachedData.current.total = data.length;
      cachedData.current.list = data;
      refreshCallback?.(data, params);
    } catch (exception) {
      console.warn(exception);
      res = null;
      resReq = null;
      resData = null;
      cachedData.current.total = 0;
      cachedData.current.list = [];
      refreshCallback?.([], params);
    }
    cachedData.current.params = rewriteParams(params, resReq);
    cachedData.current.res = {
      code: res?.code || 0,
      msg: res?.msg || '',
      req: resReq,
    };
  });

  // 获取搜索后的列表数据
  const getSearchList = useMemoizedFn((list: any[], params: ICommonEntity): any[] => {
    if (!Array.isArray(list)) return [];
    const searchKeyword = params?.[searchKeywordField] || '';
    const searchIds = params?.[searchIdsField] || [];
    if (!searchKeyword && !searchIds?.length) return list;
    if (searchIds?.length) {
      return list.filter((item) => searchIds?.includes(item?.[primaryKeyField]));
    }
    const keywords = getKeywords(searchKeyword?.toLowerCase());
    return list.filter((item) => {
      const name = item?.[nameField]?.toLowerCase();
      return keywords?.every((subItem) => name?.includes(subItem));
    });
  });

  // 获取排序后的列表数据
  const getOrderList = useMemoizedFn((list: any[], params: ICommonEntity): any[] => {
    if (!Array.isArray(list)) return [];
    if (typeof buildComparator !== 'function') return list;
    const comparator = buildComparator({
      orderSource: params?.order_source,
      orderMetric: params?.order_metric,
      orderRatio: params?.ratio_type,
      order: params?.order,
    });
    return list.sort(comparator).map((item, index) => {
      return { ...item, rank: index + 1 };
    });
  });

  // 获取分页后的列表数据
  const getPaginationList = useMemoizedFn((list: any[], params: ICommonEntity): any[] => {
    if (!Array.isArray(list)) return [];
    const pageIndex = params?.[pageIndexField] || 1;
    const pageSize = params?.[pageSizeField] || 100;
    const startIndex = (pageIndex - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return list.slice(startIndex, endIndex);
  });

  // 获取排序、分页后的列表数据
  const getPaginationAndOrderList = useMemoizedFn((params: ICommonEntity): any[] => {
    let list: any[] = cachedData?.current?.list;
    // 获取排序后的列表数据
    list = getOrderList(list, params);
    // 获取搜索后的列表数据
    list = getSearchList(list, params);
    // 获取分页后的列表数据
    list = getPaginationList(list, params);
    return Array.isArray(list) ? list : [];
  });

  // 经过前端分页、排序功能增强后的 service，用于页面调用（页面无感知，跟后端分页、排序用法一样调用该接口即可）
  return useMemoizedFn(async (params: ICommonEntity): Promise<any> => {
    const paginationAndOrderFields = [
      pageIndexField,
      pageSizeField,
      orderSourceField,
      orderMetricField,
      orderRatioField,
      orderField,
      searchKeywordField,
      searchIdsField,
    ];
    const paramsWithoutPaginationAndOrderFields = omit(params, paginationAndOrderFields);
    // 请求参数与缓存中请求参数不一致时（比较是否一致时，剔除分页、排序相关的字段），重新请求接口，刷新缓存数据
    if (!isEqual(cachedData.current.params, paramsWithoutPaginationAndOrderFields)) {
      await refreshCachedData(paramsWithoutPaginationAndOrderFields);
    }

    // 获取排序、分页后的列表数据
    const list = getPaginationAndOrderList(params);

    return {
      ...cachedData.current.res,
      req: {
        ...cachedData.current.res?.req,
        [pageIndexField]: params?.[pageIndexField] || 1,
        [pageSizeField]: params?.[pageSizeField] || 100,
        [orderSourceField]: params?.[orderSourceField] || '',
        [orderMetricField]: params?.[orderMetricField] || '',
        [orderRatioField]: params?.[orderRatioField] || SortMode.Value,
        [orderField]: params?.[orderField] || SortType.Desc,
      },
      data: {
        // 列表数据total
        [totalField]: cachedData.current.total,
        // 当前列表数据
        [listField]: list,
      },
    };
  });
}

const getKeywords = (inputValue: string, separate = true): string[] => {
  const inputValueTrim = inputValue?.trim();
  if (!inputValueTrim) return [];
  return separate ? inputValueTrim?.split('') : [inputValueTrim];
};

// 客户端排序comparator工厂方法集合
export const ClientOrderComparatorsBuilders = {
  // 获取通用指标数值、环比排序comparator builder
  getDataSourceMetricComparatorBuilder: (fieldsMapping?: {
    metricsField?: string;
    dataSourceField?: string;
    metricField?: string;
    valueField?: string;
    growthValueField?: string;
  }) => {
    const {
      metricsField = 'metric_list',
      dataSourceField = 'source',
      metricField = 'metric',
      valueField = 'value',
      growthValueField = 'growth_ratio',
    } = fieldsMapping || {};

    return (orderInfo: IOrderInfo): ((a: any, b: any) => number) => {
      const { orderSource, orderMetric, orderRatio, order } = orderInfo || {};

      return (a, b) => {
        const aMetrics = a?.[metricsField] || [];
        const bMetrics = b?.[metricsField] || [];
        const aMetric = aMetrics?.find((metric: any) => {
          return metric?.[dataSourceField] === orderSource && metric?.[metricField] === orderMetric;
        });
        const bMetric = bMetrics?.find((metric: any) => {
          return metric?.[dataSourceField] === orderSource && metric?.[metricField] === orderMetric;
        });
        const valueFieldTmp = orderRatio === SortMode.Circle ? growthValueField : valueField;
        const aValue = Number.parseFloat(aMetric?.[valueFieldTmp]);
        const bValue = Number.parseFloat(bMetric?.[valueFieldTmp]);
        const isAsc = order === SortType.Asc;
        return numberComparator(aValue, bValue, isAsc);
      };
    };
  },
};
